import React from "react";
import {
    Card,
    Modal,
    Button,
    Radio,
    Tabs,
    message,
    Select,
    Checkbox,
    Input,
    Icon
} from "antd";
import moment from "moment";
import { List, Map } from "immutable";
import PropTypes from "prop-types";
import Clipboard from "assets/clipboard.png";
import { Table } from "components";
import BankStatementUploadForm from "forms/bank-statement-upload-form";
import BankStatementUploadFormPerfios from "forms/bank-statement-upload-form-perfios";
import BankStatementMannualForm from "forms/bank-statement-analysis-mannual-form";
import CaseService from "services/case-service";
import {
    formatNormalizedStatementPatchValue,
    getAnalysisFieldDisplayValue,
    getFraudSummary,
    getSelectedStatementAnalysisIds,
    isJobSuccessStatus,
    mergeNormalizedStatementPatch,
    NORMALIZED_STATEMENT_EDIT_FIELD_MAP,
} from "utilities/bank-statement-analyser-utils";
import BankStatementAnalysisSummary from "./bank-statement-analysis-summary";
import FraudIndicatorsCell from "./fraud-indicators-cell";
import { JOB_LIST_COLUMNS, USFB_ANALYSIS_TABLE } from "./bank-statement-analyser-constants";
import Datasheet from "react-datasheet";
import "styles/bank-statement-analysis.css";

const { TabPane } = Tabs;

class BankStatementAnalyser extends React.Component {
    state = {
        uploadFiles: false,
        files: null,
        banks: [],
        perfiosBanks: [],
        analysisTypeList: new Map({}),
        activeKey: "1",
        data: [],
        perfios: true,
        normalizedStatementUpdating: false,
        normalizedStatementStats: null,
        normalizedStatementLoading: false,
        // Header "Rebuild" (force_ui) in flight.
        rebuilding: false,
        // analysis_request ids whose row-level "Redo" is in flight.
        redoingRequestIds: [],
        // Per-row Credit Limit edit: the analysis_request currently open for
        // editing, and its draft value.
        editingCreditLimitId: null,
        creditLimitDraft: "",
        // Amounts show in Lakhs by default (never rolled up to Cr — see
        // `lakhsOnly`), matching the Credit Checker. "Full value" shows the raw
        // grouped rupee figure.
        compactAmounts: true,
        // Which summaryWindows source feeds the AV./Total monthly debit/credit
        // rows: "complete" reads total_*/average_* (all transactions), "credit"
        // reads the parallel credit_total_*/credit_average_* windows (credit
        // exclusion — a credit-only view of the same windows). Every other
        // metric (ABB, bounce %, EMI bounce, Credit Limit, Fraud Count) has no
        // credit-only variant, so this only affects those four fields.
        windowMode: "complete",
    };

    componentDidMount() {
        const jobListStatus = this.props.bankStatementAnalyser?.getIn(["jobList", "status"]);
        if (!jobListStatus || jobListStatus === "UNFETCHED") {
            if (this.props.fetchJobsList && this.props.caseId) {
                this.props.fetchJobsList(this.props.caseId);
            }
        }
        this.fetchNormalizedStatement();
        CaseService.getMasterBankListPerfios("Perfios").then((response) => {
            this.setState({ perfiosBanks: response.data });
        }).catch(() => { });
        CaseService.getAnalysisTypeOptions().then(({ data }) => {
            this.setState({ analysisTypeList: new Map(data) });
        }).catch(() => { });
    }

    componentDidUpdate(prevProps) {
        const jobListStatus = this.props.bankStatementAnalyser?.getIn(["jobList", "status"]);
        const prevJobListStatus = prevProps.bankStatementAnalyser?.getIn(["jobList", "status"]);
        if (jobListStatus === "FETCHED" && prevJobListStatus === "FETCHING") {
            this.fetchNormalizedStatement({ silent: true });
        }
        if (
            prevJobListStatus === "FETCHING" &&
            (jobListStatus === "FETCHED" || jobListStatus === "FETCH_FAILED") &&
            this.state.normalizedStatementUpdating
        ) {
            this.setState({ normalizedStatementUpdating: false });
        }
    }

    refreshAnalyserData = () => {
        if (this.props.fetchJobsList && this.props.caseId) {
            this.props.fetchJobsList(this.props.caseId);
        }
        this.fetchNormalizedStatement({ silent: true });
    };

    getJobListData = () => {
        const bankStatementAnalyser = this.props.bankStatementAnalyser || new Map({});
        return bankStatementAnalyser.getIn(["jobList", "data"]) || new List([]);
    };

    toggleUploadModal = () => {
        this.setState({
            uploadFiles: !this.state.uploadFiles,
            perfios: true,
        });
        CaseService.getMasterBankListPerfios("Perfios").then((response) => {
            this.setState({ perfiosBanks: response.data });
        }).catch(() => { });
    };

    uploadBankStatements = (values) => {
        const data = new FormData();
        values.files.forEach((file) => {
            data.append("file", file);
        });
        data.append("bank_id", values.bank_id);
        data.append("case_id", this.props.caseId);
        data.append("account_type", values.account_type);
        data.append("from_date", values.from_date.format("YYYY-MM-DD"));
        data.append("to_date", values.to_date.format("YYYY-MM-DD"));
        data.append("company_id", this.props.companyId);
        data.append("document_source", 3);

        if (values.password_protected === "yes") {
            data.append("password", values.password);
        }
        if (this.state.perfios) {
            data.append("provider", true);
            this.props.createProfiosRequest(this.props.caseId, data);
        } else {
            data.append("provider", false);
            this.props.createRequest(this.props.caseId, data);
        }
        this.setState({ uploadFiles: false });
    };

    submitAnalysisDetail = (values) => {
        message.loading("Creating Request", 0);
        CaseService.createStatementAnalysis({ ...values, case_id: this.props.caseId }).then(() => {
            message.destroy();
            message.success("Job created successfully!");
            this.refreshAnalyserData();
        }).catch(() => {
            message.destroy();
            message.error("Failed to create Job");
        });
        this.setState({ uploadFiles: false });
    };

    getNoDataCard = () => (
        <Card className="padless-card">
            <div className="p-10 d-flex justify-content-space-between align-items-center">
                <div className="fb-50 d-flex flex-row align-items-center">
                    <div className="mr-5">
                        <img src={Clipboard} width="120px" alt="" />
                    </div>
                    <div style={{ color: "#333333 solid" }} className="d-flex flex-column">
                        <span style={{ fontSize: "20px", fontWeight: "600" }}>No Data Found</span>
                        <span style={{ fontSize: "12px", fontStyle: "light" }}>
                            No Bank Statements have been uploaded yet, please Upload the statements here.
                        </span>
                    </div>
                </div>
                {this.props.isSummary && this.props.recreditViewOnlyPermission ? null : (
                    <Button onClick={this.toggleUploadModal} type="primary" icon="upload">
                        Upload Bank Statements
                    </Button>
                )}
            </div>
        </Card>
    );

    headersGenerator() {
        return USFB_ANALYSIS_TABLE
            .toList()
            .map((column) => ({
                value: column.title,
                readOnly: true,
            }))
            .toArray();
    }

    rowDataGenerator(row) {
        const cells = [];
        USFB_ANALYSIS_TABLE.forEach((column, key) => {
            if (key === "month") {
                cells.push({
                    value: moment(row[key], "YYYYMM").format("MM-YYYY"),
                    key,
                    id: key,
                    readOnly: true,
                });
                return;
            }
            cells.push({
                value: row[key] ? String(row[key]) : "--",
                id: key,
                key,
                readOnly: true,
            });
        });
        return cells;
    }

    generateStatementSpreadsheet(finalData) {
        if (!finalData || !Object.keys(finalData).length || this.state.data.length) {
            return;
        }
        this.setState({
            data: [
                this.headersGenerator(),
                ...Object.values(finalData).map((row) => this.rowDataGenerator(row)),
            ],
        });
    }

    getAnalysisData = () => {
        const { bankStatementAnalyser } = this.props;
        const activeJob = this.getJobListData().toArray().find((job) => job.using);
        const requestId = activeJob?.analysis_request;
        const stats = bankStatementAnalyser && requestId
            ? bankStatementAnalyser.getIn(["statementAnalysis", requestId, "data"])
            : null;
        const analysisSummary = stats?.analysis_summary;

        if (analysisSummary) {
            const keys = Object.keys(analysisSummary);
            let monthKeys = Object.keys(analysisSummary[keys[0]] || {});
            keys.forEach((summaryKey) => {
                const currentMonths = Object.keys(analysisSummary[summaryKey] || {});
                if (currentMonths.length > monthKeys.length) {
                    monthKeys = currentMonths;
                }
            });

            const finalData = {};
            monthKeys.forEach((monthKey) => {
                finalData[monthKey] = { month: monthKey };
                keys.forEach((summaryKey) => {
                    finalData[monthKey][summaryKey] = analysisSummary[summaryKey]?.[monthKey] ?? 0;
                });
            });
            this.generateStatementSpreadsheet(finalData);
        }

        return (
            <div className="spreadsheet-table">
                <Datasheet
                    valueRenderer={(cell) => (
                        Number.isNaN(Number(cell.value))
                            ? cell.value
                            : Number(cell.value).toLocaleString("en-IN")
                    )}
                    data={this.state.data}
                />
            </div>
        );
    };

    downloadBlob = (blobData, fileName, mimeType) => {
        const blob = new Blob([blobData], { type: mimeType });
        const elem = window.document.createElement("a");
        elem.href = window.URL.createObjectURL(blob);
        elem.download = fileName;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
    };

    onStatementDwnld = (reqId) => {
        CaseService.downloadBankStatementReport(reqId).then((response) => {
            this.downloadBlob(response.data, `${reqId}_bank_statement.zip`, "octet/stream");
            message.success("File Download Successfully");
        }).catch(() => { });
    };

    onSucFailStatementDwnld = (reqId) => {
        CaseService.downloadBankStatementErrReport(reqId).then((response) => {
            this.downloadBlob(
                response.data,
                `${reqId}_bank_statement_analysis.xlsx`,
                "application/vnd.ms-excel"
            );
            message.success("File Download Successfully");
        }).catch(() => { });
    };

    handleNormalizedStatementToggle = (analysisRequestId, checked) => {
        const jobList = this.getJobListData().toArray();
        let statementAnalysisIds = getSelectedStatementAnalysisIds(jobList);

        if (checked) {
            if (!statementAnalysisIds.includes(analysisRequestId)) {
                statementAnalysisIds = [...statementAnalysisIds, analysisRequestId];
            }
        } else {
            statementAnalysisIds = statementAnalysisIds.filter((id) => id !== analysisRequestId);
        }

        this.setState({ normalizedStatementUpdating: true });
        CaseService.updateNormalizedStatement({
            case_id: this.props.caseId,
            statement_analysis_ids: statementAnalysisIds,
        })
            .then(() => {
                message.success(checked ? "Statement selected" : "Statement deselected");
                this.refreshAnalyserData();
            })
            .catch((err) => {
                message.error(
                    (err && err.non_field_errors && err.non_field_errors[0])
                    || "Failed to update statement selection"
                );
                this.setState({ normalizedStatementUpdating: false });
            });
    };

    fetchNormalizedStatement = ({ silent = false, force = false } = {}) => {
        if (!this.props.caseId) {
            return;
        }
        if (!silent) {
            this.setState({ normalizedStatementLoading: true });
        }
        return CaseService.getNormalizedStatement(this.props.caseId, force)
            .then(({ data }) => {
                this.setState({
                    normalizedStatementStats: data,
                    normalizedStatementLoading: false,
                });
            })
            .catch(() => {
                this.setState((prevState) => ({
                    normalizedStatementStats: silent
                        ? prevState.normalizedStatementStats
                        : null,
                    normalizedStatementLoading: false,
                }));
                throw new Error("normalized-statement fetch failed");
            });
    };

    // Header "Rebuild": forces the backend to recompute the normalized statement
    // from source (force_ui=true) instead of serving its cached result. Confirmed
    // first because it is the heavy path and discards the cached figures.
    handleRebuildNormalizedStatement = () => {
        Modal.confirm({
            title: "Rebuild analysis?",
            content:
                "This recomputes the whole normalized statement from source. It can take a while.",
            okText: "Rebuild",
            onOk: () => {
                this.setState({ rebuilding: true });
                return this.fetchNormalizedStatement({ force: true })
                    .then(() => {
                        message.success("Analysis rebuilt");
                        this.setState({ rebuilding: false });
                    })
                    .catch(() => {
                        message.error("Could not rebuild the analysis");
                        this.setState({ rebuilding: false });
                    });
            },
        });
    };

    // Row "Redo": re-runs the analysis for that one upload. The job list is
    // refetched so the row's status reflects the re-run.
    handleRedoAnalysis = (requestId) => {
        if (requestId == null) {
            return;
        }
        Modal.confirm({
            title: `Redo analysis for request ${requestId}?`,
            content: "The statement will be analysed again from scratch.",
            okText: "Redo",
            onOk: () => {
                this.setState((prev) => ({
                    redoingRequestIds: [...prev.redoingRequestIds, requestId],
                }));
                return CaseService.redoStatementAnalysis(requestId)
                    .then(() => {
                        message.success(`Redo started for request ${requestId}`);
                        this.refreshAnalyserData();
                    })
                    .catch(() => {
                        message.error(
                            `Could not redo analysis for request ${requestId}`
                        );
                    })
                    .finally(() => {
                        this.setState((prev) => ({
                            redoingRequestIds: prev.redoingRequestIds.filter(
                                (id) => id !== requestId
                            ),
                        }));
                    });
            },
        });
    };

    // Credit Limit is editable per-row only at the credit stages (7, 8).
    canEditCreditLimit = () => [7, 8].includes(Number(this.props.stageId));

    startCreditLimitEdit = (row) => {
        const raw = (row.ui_response || {}).credit_limit;
        this.setState({
            editingCreditLimitId: row.analysis_request,
            creditLimitDraft:
                raw !== null && raw !== undefined && raw !== "" ? String(raw) : "",
        });
    };

    // Persists one row's Credit Limit via
    // POST statement_analyzer/update-statement-analysis/<request_id>/, then
    // refreshes the analyser so the row reflects the saved value. Guarded so the
    // Enter + blur pair does not fire the request twice.
    saveCreditLimit = (requestId) => {
        if (this.state.editingCreditLimitId !== requestId || this._savingCreditLimit) {
            return;
        }
        const draft = this.state.creditLimitDraft;
        const value =
            draft === "" || draft === null || draft === undefined
                ? null
                : Number(String(draft).replace(/,/g, ""));
        if (value !== null && Number.isNaN(value)) {
            this.setState({ editingCreditLimitId: null, creditLimitDraft: "" });
            return;
        }
        this._savingCreditLimit = true;
        CaseService.updateBankStatementChequeData(requestId, { credit_limit: value })
            .then((res) => {
                this._savingCreditLimit = false;
                message.success(res?.data?.message || "Credit limit updated");
                this.setState({ editingCreditLimitId: null, creditLimitDraft: "" });
                this.refreshAnalyserData();
            })
            .catch((err) => {
                this._savingCreditLimit = false;
                this.setState({ editingCreditLimitId: null, creditLimitDraft: "" });
                const errorMessage =
                    err?.non_field_errors?.[0]
                    || (err && typeof err === "object" && Object.values(err)[0])
                    || "Failed to update credit limit";
                message.error(errorMessage);
            });
    };

    renderCreditLimitCell = (row) => {
        const requestId = row.analysis_request;
        const ui = row.ui_response || {};
        const display = getAnalysisFieldDisplayValue(
            "credit_limit",
            { credit_limit: ui.credit_limit },
            false,
            this.state.compactAmounts,
            true
        );
        if (!this.canEditCreditLimit()) {
            return display;
        }
        if (this.state.editingCreditLimitId === requestId) {
            return (
                <Input
                    autoFocus
                    size="small"
                    style={{ width: "110px" }}
                    value={this.state.creditLimitDraft}
                    onChange={(e) =>
                        this.setState({ creditLimitDraft: e.target.value })
                    }
                    onPressEnter={() => this.saveCreditLimit(requestId)}
                    onBlur={() => this.saveCreditLimit(requestId)}
                />
            );
        }
        return (
            // inline-flex (not block flex) so the column's right alignment still
            // applies and the value lines up under the "Credit Limit" header.
            <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span>{display}</span>
                <Icon
                    type="edit"
                    style={{ marginLeft: "8px", cursor: "pointer", color: "#1890ff" }}
                    onClick={() => this.startCreditLimitEdit(row)}
                />
            </span>
        );
    };

    buildJobRow = (row) => {
        // Per-row figures come from that upload's own analysis (`ui_response`),
        // not the case-level normalized statement — so each job shows what IT
        // found. Older analyses omit these keys entirely; both cells render "--"
        // rather than a misleading zero.
        const ui = row.ui_response || {};
        const fraud = getFraudSummary(ui);
        return {
            ...row,
            // The account the statement belongs to — analyser-extracted, so it
            // lives in ui_response alongside credit_limit / fraud_indicators
            // rather than on the job row itself.
            account_name: ui.account_name || "--",
            account_type: ui.account_type || "--",
            credit_limit: this.renderCreditLimitCell(row),
            // Analyser-derived like the fields above, so prefer ui_response and
            // fall back to the job row for older/other payload shapes.
            statement_relevance_tag:
                row.statement_relevance_tag !== undefined
                && row.statement_relevance_tag !== null
                    ? row.statement_relevance_tag
                    : ui.statement_relevance_tag,
            // Booleans — lives only in ui_response, and older analyses omit it
            // entirely, so `??` (not `||`) is required: a real `false` must stay
            // `false`, not get coerced into the "missing" ("--") state.
            last_30_day: ui.last_30_day ?? null,
            atleast_6m: ui.atleast_6m ?? null,
            fraud: (
                <FraudIndicatorsCell
                    count={fraud.count}
                    indicators={fraud.indicators}
                />
            ),
            select: isJobSuccessStatus(row) ? (
                <Checkbox
                    checked={!!row.using}
                    disabled={
                        this.props.recreditViewOnlyPermission
                        || this.state.normalizedStatementUpdating
                    }
                    onChange={(e) =>
                        this.handleNormalizedStatementToggle(
                            row.analysis_request,
                            e.target.checked
                        )
                    }
                />
            ) : null,
            download: (
                <a
                    onClick={() => (row.provider ? this.onStatementDwnld(row.analysis_request) : null)}
                    href={row.provider ? null : `${process.env.BASE_URL}/statement_analyzer/bank-statement/download/?request_id=${row.analysis_request}`}
                    download
                >
                    Download
                </a>
            ),
            error: row.current_status !== 3 && row.current_status !== 2 && row.current_status !== 1 ? (
                <a onClick={() => this.onSucFailStatementDwnld(row.analysis_request)} download>
                    Download
                </a>
            ) : "----",
            redo: (
                <Button
                    type="link"
                    size="small"
                    icon="redo"
                    loading={this.state.redoingRequestIds.includes(row.analysis_request)}
                    disabled={this.props.recreditViewOnlyPermission}
                    onClick={() => this.handleRedoAnalysis(row.analysis_request)}
                >
                    Redo
                </Button>
            ),
        };
    };

    editActionBankStatement = (fieldKey, val) => {
        const apiField = NORMALIZED_STATEMENT_EDIT_FIELD_MAP[fieldKey];
        if (!apiField || !this.props.caseId) {
            return;
        }
        const patchValue = formatNormalizedStatementPatchValue(val);
        CaseService.patchNormalizedStatement({
            case_id: this.props.caseId,
            [apiField]: patchValue,
        })
            .then((res) => {
                if (res.status === 200 || res.status === 204) {
                    message.success(res.data?.message || "Updated successfully");
                    this.setState((prevState) => ({
                        normalizedStatementStats: mergeNormalizedStatementPatch(
                            prevState.normalizedStatementStats,
                            apiField,
                            patchValue
                        ),
                    }));
                    this.fetchNormalizedStatement({ silent: true });
                }
            })
            .catch((err) => {
                const errorMessage = err?.non_field_errors?.[0]
                    || (err && Object.values(err)[0])
                    || "Failed to update";
                message.error(errorMessage);
            });
    };

    getSortedJobList = () => (
        this.getJobListData().sort((a, b) => b.create_date - a.create_date)
    );

    renderJobsCard = () => {
        const jobList = this.getSortedJobList();
        const jobCount = jobList.size || 0;
        return (
            <Card
                className="padless-card bsa-card"
                title={!this.props.isSummary ? (
                    <span className="bsa-header__title">
                        Bank Statement Analyser
                        {jobCount ? (
                            <span className="bsa-header__count">
                                {`(${jobCount})`}
                            </span>
                        ) : null}
                    </span>
                ) : null}
                extra={!this.props.isSummary ? (
                    <span className="bsa-header__actions">
                        <Radio.Group
                            key="units"
                            size="small"
                            value={this.state.compactAmounts}
                            onChange={(e) =>
                                this.setState({ compactAmounts: e.target.value })
                            }
                        >
                            <Radio.Button value={true}>In Lakhs</Radio.Button>
                            <Radio.Button value={false}>Full value</Radio.Button>
                        </Radio.Group>
                        <Radio.Group
                            key="window-mode"
                            size="small"
                            value={this.state.windowMode}
                            onChange={(e) =>
                                this.setState({ windowMode: e.target.value })
                            }
                        >
                            <Radio.Button value="complete">Complete</Radio.Button>
                            <Radio.Button value="credit">Credit</Radio.Button>
                        </Radio.Group>
                        <Button
                            onClick={this.handleRebuildNormalizedStatement}
                            disabled={this.props.recreditViewOnlyPermission}
                            loading={this.state.rebuilding}
                            key="rebuild"
                            title="Recompute the whole analysis from source"
                        >
                            Rebuild Analysis
                        </Button>
                        <Button
                            onClick={this.toggleUploadModal}
                            disabled={this.props.recreditViewOnlyPermission}
                            key="upload"
                            type="primary"
                        >
                            Upload Bank Statements
                        </Button>
                    </span>
                ) : null}
            >
                <BankStatementAnalysisSummary
                    normalizedStatementStats={this.state.normalizedStatementStats}
                    normalizedStatementLoading={this.state.normalizedStatementLoading}
                    bankCode={this.props.bankCode}
                    adminGroup={this.props.adminGroup}
                    userGroup={this.props.userGroup}
                    recreditViewOnlyPermission={this.props.recreditViewOnlyPermission}
                    compact={this.state.compactAmounts}
                    /* Lakhs only — amounts are never rolled up to Cr, matching
                       the Credit Checker's banking card. */
                    lakhsOnly={true}
                    windowMode={this.state.windowMode}
                    onEditField={this.editActionBankStatement}
                />
                {this.props.bankCode === "USFB" ? this.getAnalysisData() : null}
                {!this.props.isSummary ? (
                    <Table
                        columns={JOB_LIST_COLUMNS}
                        data={jobList.map(this.buildJobRow)}
                        title="Jobs List"
                        showFilters={false}
                        showColumnSelector={false}
                        noRowSelection
                    />
                ) : null}
            </Card>
        );
    };

    onTabChange = (key) => {
        this.setState({ activeKey: key });
    };

    handlePerfiosSelect = (value) => {
        if (value === "Finnov") {
            CaseService.getMasterBankListPerfios("Finnov").then((response) => {
                this.setState({ banks: response.data, perfios: false });
            }).catch(() => { });
            return;
        }
        CaseService.getMasterBankListPerfios("Perfios").then((response) => {
            this.setState({ perfiosBanks: response.data, perfios: true });
        }).catch(() => { });
    };

    render() {
        return (
            <React.Fragment>
                <Modal
                    visible={this.state.uploadFiles}
                    destroyOnClose
                    closable
                    footer={null}
                    onCancel={this.toggleUploadModal}
                >
                    <h3>Select Provider: </h3>
                    <Select
                        style={{ width: "140px" }}
                        defaultValue="Perfios"
                        onChange={this.handlePerfiosSelect}
                    >
                        <Select.Option value="Perfios">Perfios</Select.Option>
                    </Select>
                    <Tabs
                        className="statement-analysis-modal"
                        style={{ marginTop: "20px" }}
                        defaultActiveKey="1"
                        onChange={this.onTabChange}
                    >
                        <TabPane tab="Auto" key="1">
                            {this.state.perfios ? (
                                <BankStatementUploadFormPerfios
                                    banks={this.state.perfiosBanks}
                                    onSubmit={this.uploadBankStatements}
                                    analysisTypeList={this.state.analysisTypeList}
                                    recreditViewOnlyPermission={this.props.recreditViewOnlyPermission}
                                />
                            ) : (
                                <BankStatementUploadForm
                                    banks={this.state.banks}
                                    onSubmit={this.uploadBankStatements}
                                    analysisTypeList={this.state.analysisTypeList}
                                    recreditViewOnlyPermission={this.props.recreditViewOnlyPermission}
                                />
                            )}
                        </TabPane>
                        {!this.state.perfios ? (
                            <TabPane tab="Manual" key="2">
                                <BankStatementMannualForm
                                    analysisTypeList={this.state.analysisTypeList}
                                    onSubmit={this.submitAnalysisDetail}
                                />
                            </TabPane>
                        ) : null}
                    </Tabs>
                </Modal>
                {this.getJobListData().size > 0 ? this.renderJobsCard() : this.getNoDataCard()}
            </React.Fragment>
        );
    }
}

BankStatementAnalyser.propTypes = {
    createRequest: PropTypes.func,
    createProfiosRequest: PropTypes.func,
    fetchJobsList: PropTypes.func,
    caseId: PropTypes.number,
    companyId: PropTypes.number,
    bankStatementAnalyser: PropTypes.object,
    isSummary: PropTypes.bool,
    bankCode: PropTypes.string,
    adminGroup: PropTypes.object,
    // user.data.groups — Credit Manager (id 7) may override the summary fields.
    userGroup: PropTypes.array,
    recreditViewOnlyPermission: PropTypes.object,
    // Case stage — Credit Limit is editable per-row only at stages 7 and 8.
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default BankStatementAnalyser;
